import {
	ChoiceGroupCompat as ChoiceGroup,
	DefaultButtonCompat as DefaultButton,
	IconButtonCompat as IconButton,
	PanelCompat as Panel,
	PanelType,
	PivotCompat as Pivot,
	PivotItem,
	PrimaryButtonCompat as PrimaryButton,
	ScrollablePaneCompat as ScrollablePane,
	SearchBoxCompat as SearchBox,
	SelectionMode,
	SeparatorCompat as Separator,
	StackCompat as Stack,
	TextCompat as Text,
	mergeStyleSets,
	useTheme,
} from 'components/FluentUICompat';
import type {
	IButtonStyles,
	IChoiceGroupOption,
	IScrollablePaneStyles,
	IStackStyles,
	Theme,
} from 'components/FluentUICompat';
import {useBoolean} from '@fluentui/react-hooks';
import {useUserContext} from 'authentication/UserContext';
import {ConfirmUpdateDialog, useDialogState} from 'components/Dialogs';
import {
	ActionButton,
	EntityList,
	EntityListColumn,
	SearchableComboBox,
} from 'components';
import {AttachmentsField} from 'components/AttachmentsField';
import {renderParagraph} from 'components/EntityList/ColumnRenderers';
import {EntityNameField} from 'components/EntityNameField';
import {KeywordFields} from 'components/Keywords/keywordItem.types';
import {LoadWrapper} from 'components/LoadWrapper';
import {useGetConvolutesQuery} from 'features/AdminSection/hooks/convolutes.generated';
import {useGetGdprRuleQuery} from 'features/AdminSection/hooks/gdprRules.generated';
import {useGetAllKeywordsQuery} from 'features/AdminSection/hooks/keywords.generated';
import {useGetAllRegulationsQuery} from 'features/Regulations/hooks';
import {RegulatoryDetailsPageFormMode} from 'features/RegulatoryDocuments/RegDocDetailsPage/RegDocDetailsPage.types';
import {useRegulatoryDocumentsContext} from 'features/RegulatoryDocuments/context';
import {useGetexMutations} from 'features/RegulatoryDocuments/RegDocDetailsPage/useGetexMutations';
import {useCreateRequirementMutation} from 'features/RegulatoryDocuments/hooks/useCreateRequirement.generated';
import {
	GetAddRequirementsFormDataQuery,
	useGetMyClusterRequirementsQuery,
} from 'features/RegulatoryDocuments/hooks/useGetAddRequirementsFormData.generated';
import {GetRegulatoryDocumentDetailsDocument} from 'features/RegulatoryDocuments/hooks/useGetRegulatoryDocumentDetails.generated';
import {
	GetRegulatoryDocumentsWithParagraphsQuery,
	useGetRegulatoryDocumentsWithParagraphsLazyQuery,
} from 'features/RegulatoryDocuments/hooks/useGetRegulatoryDocumentsWithParagraphs.generated';
import {
	useGetRequirementsWithAllocationLazyQuery,
	useGetRequirementsWithoutAllocationLazyQuery,
} from 'features/RegulatoryDocuments/hooks/useGetRequirementsAllocation.generated';
import {RequirementUtilsService} from 'features/Requirements/RequirementUtils.service';
import {RequirementsTooltipTranslationProvider} from 'features/Requirements/RequirementsTooltipTranslationProvider';
import {
	CommonFormElementsOfRequirements,
	CompatibleFormFields,
} from 'features/Requirements/components/CommonFormElementsOfRequirements';
import {useUpdateRequirementAttachmentsMutation} from 'features/Requirements/hooks/requirementAttachments.generated';
import {useGetRequirementsFormDataQuery} from 'features/Requirements/hooks/requirements.generated';
import {useRequirementColumns} from 'features/Requirements/hooks/useRequirementColumns';
import {useRequirementsTranslation} from 'features/Requirements/hooks/useRequirementsTranslation';
import {ProviderThatEnablesGettingTooltipsFromContext} from 'features/localizedTooltips';
import {mapToRef} from 'helpers';
import {
	useCommand,
	useFileUpload,
	useNotificationBar,
	useSelection,
} from 'hooks';
import {uniqBy} from 'lodash';
import React, {useMemo, useState} from 'react';
import {useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {useSearchParams} from 'react-router-dom';
import {
	ConvoluteType,
	DriveVariant,
	RegulatoryDocumentParagraph,
	Requirement,
	RequirementStatus,
	UserRole,
	VehicleCategory,
} from 'types';
import {useParagraphsContext} from './ParagraphsContext';
import {useCloneRequirement} from 'features/Requirements/hooks/useCloneRequirement';
import {QueryResult} from '@apollo/client';
import {useRequirementsVersions} from 'features/Requirements/hooks/useRequirementsVersions';
import {numericalSort} from 'helpers/sortHelpers';
import {RequirementFromListPage} from 'features/Requirements/requirementPage.types';

type FilterOptions = {
	filterByRegulation: boolean;
	filterByKeyword: boolean;
	keyword?: string;
	searchValue?: string;
};

type QueryRequirement = GetAddRequirementsFormDataQuery['requirements'][number];

/**
 * We use this to verify that the requirement has the necessary data to render
 * the Entity List columns' values.
 */
interface RequirementForEntityList extends Omit<QueryRequirement, 'keywords'> {
	keywords: KeywordFields[];
}

const {createCommonCreationInputFields} = new RequirementUtilsService();

type PossibleRegDocData =
	QueryResult<GetRegulatoryDocumentsWithParagraphsQuery>['data'];

export const AssignRequirementsForm = () => {
	const {t} = useTranslation('features/regulatorydocuments', {
		keyPrefix: 'AssignRequirementsForm',
	});

	const {t: getRequirementsTranslation} = useRequirementsTranslation();

	const theme = useTheme();
	const classNames = getClassNames(theme);

	const {assignParagraphsRequirements} = useGetexMutations();
	const [createRequirement] = useCreateRequirementMutation();
	const [updateAttachments] = useUpdateRequirementAttachmentsMutation();
	const {cloneRequirement} = useCloneRequirement();
	const [showFilter, setShowFilter] = useState(false);
	const [keyword, setKeyword] = useState<string | number>();
	const [searchValue, setSearchValue] = useState('');

	const {selectedParagraphs} = useParagraphsContext();
	const {selectedRegulatoryDocument} = useRegulatoryDocumentsContext();

	// Frozen snapshots — captured once when the panel opens so that background
	// data refreshes don't affect the user's in-progress form interaction.
	const [frozenParagraphs, setFrozenParagraphs] = useState(selectedParagraphs);
	const [frozenRegulatoryDocument, setFrozenRegulatoryDocument] = useState(
		selectedRegulatoryDocument,
	);

	type FormFields = CompatibleFormFields & Pick<Requirement, 'name'>;

	const {handleSubmit, control, reset, setValue} = useForm<FormFields>({
		reValidateMode: 'onSubmit',
		mode: 'all',
	});

	const [mode, setMode] = React.useState<RegulatoryDetailsPageFormMode>(
		RegulatoryDetailsPageFormMode.None,
	);
	const [
		createNewMode,
		{setTrue: changeToCreateMode, setFalse: changeToAssignMode},
	] = useBoolean(false);

	React.useEffect(() => {
		if (mode === RegulatoryDetailsPageFormMode.Assign) {
			setFrozenParagraphs(selectedParagraphs);
			setFrozenRegulatoryDocument(selectedRegulatoryDocument);
		}
	}, [mode]);

	React.useEffect(() => {
		reset({});
	}, [frozenParagraphs]);

	const {isAdmin, isVex, isVexSupport, isBav, isAvk} = useUserContext();
	const isAvkOrBav = isBav || isAvk;
	const {data, loading} = useGetMyClusterRequirementsQuery({
		skip: !isAdmin && !isVex,
	});
	const [
		getReqWithAllocationLazy,
		{
			data: requirementsWithAllocation,
			loading: loadingRequirementsWithAllocation,
		},
	] = useGetRequirementsWithAllocationLazyQuery({skip: !isAvkOrBav} as any);
	const [
		getReqWithoutAllocationLazy,
		{
			data: requirementsWithoutAllocation,
			loading: loadingRequirementsWithoutAllocation,
		},
	] = useGetRequirementsWithoutAllocationLazyQuery({skip: !isAvkOrBav} as any);
	const {data: regulationData} = useGetAllRegulationsQuery();
	const {data: keywordData} = useGetAllKeywordsQuery();
	const {loading: formDataLoading, data: formData} =
		useGetRequirementsFormDataQuery();
	const {hasEditableChildren, filterOutPastMinorVersionOfRequirements} =
		useRequirementsVersions();

	const [getRegDocs, {data: regDocData}] =
		useGetRegulatoryDocumentsWithParagraphsLazyQuery();

	const {data: convolutes} = useGetConvolutesQuery();
	const convolute = React.useMemo(
		() =>
			convolutes?.convolutes?.find(
				c => c.convoluteType === ConvoluteType.Requirement,
			),
		[convolutes],
	);

	const {setMessage} = useNotificationBar();

	const {data: gdprRule} = useGetGdprRuleQuery({
		variables: {
			id: convolute?.gdprRule?.id ?? '',
		},
	});

	const rule = useMemo(() => gdprRule?.gdprRule, [gdprRule]);

	const {myVexClusters} = useUserContext();

	const requirementData: QueryRequirement[] = useMemo(
		() => data?.myRequirements ?? [],
		[data],
	);

	const requirementDataWithAllocation: QueryRequirement[] = React.useMemo(
		() => requirementsWithAllocation?.requirementsWithAllocation ?? [],
		[requirementsWithAllocation],
	);

	const requirementDataWithoutAllocation: QueryRequirement[] = React.useMemo(
		() => requirementsWithoutAllocation?.requirementsWithoutAllocation ?? [],
		[requirementsWithoutAllocation],
	);

	const keywordOptions = React.useMemo(() => {
		return numericalSort(keywordData?.keywords ?? [], 'sortPriority').map(
			k => ({
				key: k.id,
				text: k.name,
			}),
		);
	}, [keywordData]);

	const regulationOptions = React.useMemo(() => {
		return (
			regulationData?.regulations.map(r => ({
				key: r.id,
				text: `${r.regulationNumber} – ${r.name}`,
			})) ?? []
		).sort((a, b) => a.text.localeCompare(b.text));
	}, [regulationData]);

	const [regulationText, setRegulationText] = React.useState('');
	const [selectedRegulationId, setSelectedRegulationId] = React.useState<
		string | undefined
	>();

	const filteredRegulationOptions = React.useMemo(() => {
		if (!regulationText) return regulationOptions;
		const lower = regulationText.toLowerCase();
		return regulationOptions.filter(o => o.text.toLowerCase().includes(lower));
	}, [regulationOptions, regulationText]);

	const [keywordText, setKeywordText] = React.useState('');
	const [selectedKeywordId, setSelectedKeywordId] = React.useState<
		string | undefined
	>();

	const filteredKeywordOptions = React.useMemo(() => {
		if (!keywordText) return keywordOptions;
		const lower = keywordText.toLowerCase();
		return keywordOptions.filter(o => o.text.toLowerCase().includes(lower));
	}, [keywordOptions, keywordText]);

	const clearRegulation = React.useCallback(() => {
		setSelectedRegulationId(undefined);
		setRegulationText('');
		setFilterByRegulation(false);
	}, []);

	const clearKeyword = React.useCallback(() => {
		setSelectedKeywordId(undefined);
		setKeywordText('');
		setFilterByKeyword(false);
		setKeyword(undefined);
	}, []);

	const [filterByKeyword, setFilterByKeyword] = React.useState(false);
	const [filterByRegulation, setFilterByRegulation] = React.useState(false);

	const [selectedRequirements, setSelectedRequirements] = React.useState<
		Requirement[]
	>([]);

	const selectedFinalRequirements = React.useMemo(
		() =>
			selectedRequirements?.filter(
				sr => sr.status === RequirementStatus.Final,
			) || [],
		[selectedRequirements],
	);

	const selectedRequirementsRequiresVersioning = React.useMemo(
		() =>
			selectedRequirements?.filter(
				sr =>
					sr.status === RequirementStatus.Final ||
					sr.status === RequirementStatus.Approval,
			) || [],
		[selectedRequirements],
	);

	const selectedNonFinalRequirements = React.useMemo(
		() =>
			selectedRequirements?.filter(
				sr => sr.status !== RequirementStatus.Final,
			) || [],
		[selectedRequirements],
	);

	const [versionDialogState, showVersionDialog, hideVersionDialog] =
		useDialogState();

	const [selection] = useSelection<Requirement>({
		onSelectionChanged(selectedItems) {
			setSelectedRequirements(selectedItems);
		},
		getKey: item => item.id,
	});

	const {attachments, FileUploadComponent} = useFileUpload(undefined);

	const onRequirementCreated = React.useCallback(
		async (createRes: any) => {
			const createdRequirementId =
				createRes.data?.createRequirement.requirement?.id;
			if (createdRequirementId) {
				if (attachments) {
					await updateAttachments({
						variables: {
							input: {
								attachments: attachments.map(f => ({
									attachmentId: f.file.name,
									file: f.file,
									categoryId: f.attachmentCategoryId,
								})),
								requirementId: createdRequirementId,
							},
						},
					});
				}

				await assignParagraphsRequirements(
					frozenRegulatoryDocument?.id ?? '',
					[{id: createdRequirementId}],
					frozenParagraphs?.map(p => p.id) ?? [],
					[GetRegulatoryDocumentDetailsDocument],
				);
			}
		},
		[attachments, frozenParagraphs, frozenRegulatoryDocument],
	);

	const findByRequirementIndex = React.useCallback(
		(sp: RegulatoryDocumentParagraph, req: Requirement) =>
			sp.requirements.findIndex(spReq => spReq.id === req.id) === -1,
		[],
	);
	const filterSelectedRequirements = React.useCallback(
		(selectedFinalRequirements: Requirement[]) =>
			selectedFinalRequirements.filter(req => {
				return frozenParagraphs.some(sp => findByRequirementIndex(sp, req));
			}),
		[frozenParagraphs],
	);

	const handleRequirementVersioning = React.useCallback(async () => {
		const newRequirements = filterSelectedRequirements(
			selectedFinalRequirements,
		);
		/* eslint-disable no-await-in-loop */
		for (const nr of newRequirements) {
			await cloneRequirement(
				nr,
				[GetRegulatoryDocumentDetailsDocument],
				rule,
				undefined,
				frozenRegulatoryDocument?.id ?? '',
				frozenParagraphs.map(sp => sp.id),
			);
		}
	}, [
		selectedFinalRequirements,
		frozenRegulatoryDocument,
		frozenParagraphs,
		cloneRequirement,
		GetRegulatoryDocumentDetailsDocument,
	]);

	const onSaveAsync = React.useCallback(async () => {
		await handleSubmit(async formFields => {
			if (createNewMode) {
				await createRequirement({
					variables: {
						input: createCommonCreationInputFields(formFields, rule),
					},
				}).then(onRequirementCreated);
			} else {
				// Non-Final requirements get assigned directly
				// Final requirements will get shadow versions assigned
				if (
					frozenRegulatoryDocument?.id &&
					selectedNonFinalRequirements.length > 0
				) {
					await assignParagraphsRequirements(
						frozenRegulatoryDocument.id,
						mapToRef(selectedNonFinalRequirements),
						frozenParagraphs?.map(p => p.id) ?? [],
						[GetRegulatoryDocumentDetailsDocument],
					);
				}
				await handleRequirementVersioning();
			}

			setMode(RegulatoryDetailsPageFormMode.None);
			selection.setAllSelected(false);
			setFilterByKeyword(false);
			setFilterByRegulation(false);
			setSearchValue('');
			setKeyword(undefined);
			setRegulationText('');
			setSelectedRegulationId(undefined);
			setKeywordText('');
			setSelectedKeywordId(undefined);
			setMessage(
				t('AssignRequirementUpdated', {
					selectedParagraphs: frozenParagraphs.length.toString(),
				}),
			);
			reset({});
		})();
	}, [
		mode,
		createNewMode,
		selectedRequirementsRequiresVersioning,
		frozenParagraphs,
		frozenRegulatoryDocument,
		attachments,
		rule,
	]);

	const handleSaveClick = React.useCallback(async () => {
		if (!createNewMode && selectedRequirementsRequiresVersioning.length > 0) {
			showVersionDialog();
			return;
		}
		await onSaveAsync();
	}, [
		createNewMode,
		selectedRequirementsRequiresVersioning,
		onSaveAsync,
		showVersionDialog,
	]);

	const onConfirmVersioning = React.useCallback(async () => {
		hideVersionDialog();
		await onSaveAsync();
	}, [onSaveAsync, hideVersionDialog]);

	const handleCancelClick = React.useCallback(() => {
		setMode(RegulatoryDetailsPageFormMode.None);
		setFilterByKeyword(false);
		setFilterByRegulation(false);
		setSearchValue('');
		setKeyword(undefined);
		setRegulationText('');
		setSelectedRegulationId(undefined);
		setKeywordText('');
		setSelectedKeywordId(undefined);
		reset({});
	}, []);

	const onPivotItemClick = (item?: any) => {
		if (item?.props.itemKey === 'assign') {
			changeToAssignMode();
		} else if (item?.props.itemKey === 'create') {
			changeToCreateMode();
		}
	};

	const handleOnClear = React.useCallback(() => {
		setSearchValue('');
	}, []);

	const onRenderFooterContent: any = React.useCallback(
		() => (
			<form>
				<ActionButton styles={buttonStyles} onClick={handleSaveClick}>
					{createNewMode
						? t('CreateRequirementButton')
						: t('AssignRequirementButton', {
								count: selectedRequirements.length,
						  })}
				</ActionButton>
				<DefaultButton onClick={handleCancelClick}>
					{t('CancelButton')}
				</DefaultButton>
			</form>
		),
		[
			selectedRequirements,
			createNewMode,
			attachments,
			handleSaveClick,
			handleCancelClick,
		],
	);

	const [searchParams] = useSearchParams();

	const filter = searchParams.get('filter') ?? '';

	// VEX users may only assign SH-requirements to segments linked to one of their own VEX clusters.
	// if multiple paragraphs are selected, the assign button will be disabled if at least one of the paragraphs is linked to a VEX cluster that is not in the user's VEX clusters.
	const myVexClusterIds = React.useMemo(
		() => new Set(myVexClusters?.map(c => c.id) ?? []),
		[myVexClusters],
	);
	const restrictToMyVexClusters = isVex && !isAdmin;
	const hasParagraphOutsideMyVexClusters = React.useMemo(() => {
		if (!restrictToMyVexClusters) return false;
		return selectedParagraphs.some(
			p =>
				!(p.keywordAssignments ?? []).some(ka =>
					(ka.vexClusterAssignments ?? []).some(vca =>
						myVexClusterIds.has(vca.vexCluster?.id),
					),
				),
		);
	}, [restrictToMyVexClusters, selectedParagraphs, myVexClusterIds]);

	/* Requirment button, Regulatory Document */
	useCommand(
		{
			key: 'assign',
			text: t('Assign'),
			priority: 1,
			iconProps: {
				iconName: 'Add',
			},
			disabled:
				selectedParagraphs.length === 0 ||
				isVexSupport ||
				hasParagraphOutsideMyVexClusters,
			onClick: () => setMode(RegulatoryDetailsPageFormMode.Assign),
			roles: [UserRole.SystemAdministrator, UserRole.Vex],
			title: hasParagraphOutsideMyVexClusters
				? t('AssignRequirementsTooltipNotInMyVexCluster')
				: t('AssignRequirementsTooltip'),
		},
		[
			mode,
			selectedParagraphs,
			isAdmin,
			isVexSupport,
			filter,
			hasParagraphOutsideMyVexClusters,
		],
	);

	const columns: EntityListColumn[] = useRequirementColumns();

	const paragraphsWithTags = React.useMemo(
		() => (selectedParagraphs?: RegulatoryDocumentParagraph[]) =>
			selectedParagraphs?.map(selectedParagraph => (
				<Stack key={selectedParagraph.id}>
					{renderParagraph(selectedParagraph, t)}
					<div className={classNames.requirementsWrapper}>
						{selectedParagraph.requirements?.map(requirement => (
							<Text
								className={classNames.requirement}
								key={`${selectedParagraph.id}${requirement.id}`}
							>
								{requirement.name}
							</Text>
						))}
					</div>
					<Separator />
				</Stack>
			)),
		[selectedParagraphs],
	);

	const options: IChoiceGroupOption[] = [
		{key: 'A', text: t('MyVexRequirements')},
		{key: 'B', text: t('RequirementsWithoutAllocation')},
		{key: 'C', text: t('RequirementsWithAllocation')},
	];

	const [selectedKey, setSelectedKey] = React.useState<string | undefined>('A');

	const onChange = React.useCallback(
		(ev: React.SyntheticEvent<HTMLElement>, option: IChoiceGroupOption) => {
			switch (option.key) {
				case 'B':
					getReqWithoutAllocationLazy();
					break;
				case 'C':
					getReqWithAllocationLazy();
					break;
				default:
					break;
			}
			setSelectedKey(option.key);
		},
		[isBav, isAvk],
	);

	const setFilteredData = React.useCallback((): RequirementForEntityList[] => {
		let data = requirementData;
		if (selectedKey === 'B') {
			data = requirementDataWithoutAllocation;
		}

		if (selectedKey === 'C') {
			data = requirementDataWithAllocation;
		}

		return (
			filterOutPastMinorVersionOfRequirements(
				data,
			) as RequirementForEntityList[]
		).filter(req => !hasEditableChildren(req as RequirementFromListPage));
	}, [
		selectedKey,
		requirementData,
		requirementDataWithoutAllocation,
		requirementDataWithAllocation,
	]);

	const onAggregateFromParagraphsClick = React.useCallback(() => {
		const driveVariants: DriveVariant[] = uniqBy(
			frozenParagraphs.flatMap(p => p.driveVariants),
			p => p.id,
		);
		const vehicleCategories: VehicleCategory[] = uniqBy(
			frozenParagraphs.flatMap(p => p.vehicleCategories),
			p => p.id,
		);

		setValue('driveVariants', driveVariants);
		setValue('vehicleCategories', vehicleCategories);
	}, [frozenParagraphs]);

	const reqs: RequirementForEntityList[] = React.useMemo(
		() =>
			filterRequirements(setFilteredData(), myVexClusters, regDocData, {
				filterByRegulation,
				filterByKeyword,
				keyword,
				searchValue,
			} as any),
		[
			setFilteredData,
			myVexClusters,
			regDocData,
			keyword,
			filterByKeyword,
			filterByRegulation,
			searchValue,
			selectedKey,
		],
	);
	return (
		<>
			<Panel
				type={PanelType.extraLarge}
				isFooterAtBottom={true}
				isOpen={mode === RegulatoryDetailsPageFormMode.Assign}
				onDismiss={() => {
					if (!versionDialogState.hidden) return;
					handleCancelClick();
				}}
				closeButtonAriaLabel='Close'
				onRenderFooterContent={onRenderFooterContent}
			>
				<LoadWrapper loading={loading || formDataLoading}>
					<Stack horizontal tokens={tokens} styles={{root: {height: '100%'}}}>
						<Stack styles={paragraphStackStyles} tokens={tokens} wrap>
							<ScrollablePane styles={selectedParagraphsScrollablePaneStyles}>
								<Stack.Item>
									<Text variant='xLarge'>{t('SelectedParagraphsHeader')}</Text>
								</Stack.Item>
								<Stack.Item>
									{paragraphsWithTags(frozenParagraphs)}
								</Stack.Item>
							</ScrollablePane>
						</Stack>
						<Stack styles={requirementStackStyles} tokens={tokens}>
							<ScrollablePane styles={assignRequirementsScrollablePaneStyles}>
								<Stack.Item>
									<Text variant='xLarge'>{t('AssignRequirementsHeader')}</Text>
								</Stack.Item>
								<Pivot
									onLinkClick={onPivotItemClick}
									defaultSelectedKey={createNewMode ? 'create' : 'assign'}
								>
									<PivotItem headerText={t('MyRequirements')} itemKey='assign'>
										<Stack tokens={tokens}>
											<Stack horizontal tokens={tokens}>
												<Stack.Item styles={iconButtonStyles}>
													<IconButton
														iconProps={{
															iconName: 'QueryList',
														}}
														id={'filterButton'}
														onClick={() => setShowFilter(!showFilter)}
													/>
												</Stack.Item>
												<Stack.Item styles={searchboxStyles}>
													<SearchBox
														placeholder={t('SearchPlaceholder')}
														onSearch={value => {
															setSearchValue(value.trim());
														}}
														onClear={handleOnClear}
													/>
												</Stack.Item>
											</Stack>
											{showFilter && (
												<Stack>
													<Stack.Item>
														<ChoiceGroup
															defaultSelectedKey='A'
															options={options}
															onChange={onChange as any}
															selectedKey={selectedKey}
														/>
													</Stack.Item>
													<Stack.Item>
														<Stack.Item>
															<SearchableComboBox
																label={t('Regulation')}
																options={filteredRegulationOptions}
																selectedKey={selectedRegulationId ?? null}
																text={regulationText}
																onInputValueChange={inputText => {
																	setRegulationText(inputText);
																	setSelectedRegulationId(undefined);
																	setFilterByRegulation(false);
																}}
																onChange={(_, option) => {
																	if (option) {
																		const id = option.key as string;
																		setSelectedRegulationId(id);
																		setRegulationText(option.text);
																		setFilterByRegulation(true);
																		getRegDocs({variables: {regulationId: id}});
																	}
																}}
																onClear={clearRegulation}
															/>
														</Stack.Item>
													</Stack.Item>
													<Stack.Item>
														<Stack.Item>
															<SearchableComboBox
																label={t('Keywords')}
																options={filteredKeywordOptions}
																selectedKey={selectedKeywordId ?? null}
																text={keywordText}
																onInputValueChange={inputText => {
																	setKeywordText(inputText);
																	setSelectedKeywordId(undefined);
																	setFilterByKeyword(false);
																	setKeyword(undefined);
																}}
																onChange={(_, option) => {
																	if (option) {
																		const id = option.key as string;
																		setSelectedKeywordId(id);
																		setKeywordText(option.text);
																		setFilterByKeyword(true);
																		setKeyword(id);
																	}
																}}
																onClear={clearKeyword}
															/>
														</Stack.Item>
													</Stack.Item>
												</Stack>
											)}
											<Stack.Item>
												<RequirementsTooltipTranslationProvider>
													<EntityList
														items={reqs}
														columns={columns}
														aria-rowcount
														selectionMode={SelectionMode.multiple}
														selection={selection}
														sticky={true}
													/>
												</RequirementsTooltipTranslationProvider>
											</Stack.Item>
										</Stack>
									</PivotItem>
									<PivotItem
										headerText={t('CreateRequirement')}
										itemKey='create'
									>
										<Stack tokens={tokens}>
											<RequirementsTooltipTranslationProvider>
												<ProviderThatEnablesGettingTooltipsFromContext>
													<Stack.Item>
														<EntityNameField
															control={control}
															t={getRequirementsTranslation}
															labelTranslationKey='Name'
															translationKeyOfRequiredMsg='RequiredField'
														/>
													</Stack.Item>
													<CommonFormElementsOfRequirements
														control={control}
														formData={formData}
														selectableVexClusters={myVexClusters}
														id={null}
														aggregationBtn={
															<PrimaryButton
																onClick={onAggregateFromParagraphsClick}
																styles={aggregateButtonStyles}
															>
																{t('AggregateFromParagraphs')}
															</PrimaryButton>
														}
													/>
													<Separator />
													<Stack.Item>
														<AttachmentsField
															t={t}
															FileUploadComponent={FileUploadComponent}
														/>
													</Stack.Item>
												</ProviderThatEnablesGettingTooltipsFromContext>
											</RequirementsTooltipTranslationProvider>
										</Stack>
									</PivotItem>
								</Pivot>
							</ScrollablePane>
						</Stack>
					</Stack>
				</LoadWrapper>
			</Panel>
			<ConfirmUpdateDialog
				{...versionDialogState}
				onConfirm={onConfirmVersioning}
				dialogContentProps={{
					title: t('VersioningDialogTitle'),
					subText: t('VersioningDialogSubText', {
						count: selectedRequirementsRequiresVersioning.length,
					}),
				}}
				confirmButtonLabel={t('VersioningDialogConfirm')}
			/>
		</>
	);
};

function filterRequirements(
	requirementData: RequirementForEntityList[],
	myVexClusters: {id: string}[],
	regDocData: PossibleRegDocData,
	filterOptions: FilterOptions,
): RequirementForEntityList[] {
	let filteredRequirements = requirementData;

	if (filterOptions.filterByRegulation && regDocData) {
		filteredRequirements = filteredRequirements.filter(requirement =>
			regDocData.regulation?.regulatoryDocuments?.some(regDoc =>
				regDoc.paragraphs.some(paragraph =>
					paragraph.requirements.some(req => req.id === requirement.id),
				),
			),
		);
	}

	if (filterOptions.filterByKeyword && filterOptions.keyword) {
		filteredRequirements = filteredRequirements.filter(requirement =>
			requirement.keywords.some(k => k.id === filterOptions.keyword),
		);
	}

	if (filterOptions.searchValue && filterOptions.searchValue.length > 0) {
		filteredRequirements = filteredRequirements.filter(requirement => {
			const searchValue = (filterOptions.searchValue as any).toLowerCase();

			return (
				requirement.name.toLowerCase().includes(searchValue) ||
				requirement.requirementId?.toLowerCase().includes(searchValue) ||
				requirement.definition?.toLowerCase().includes(searchValue)
			);
		});
	}

	return filteredRequirements;
}

const getClassNames = (theme: Theme) =>
	mergeStyleSets({
		requirementsWrapper: {
			display: 'flex !important',
			flexWrap: 'wrap',
		},
		requirement: {
			borderRadius: '10px',
			color: theme.palette.neutralSecondary,
			background: theme.palette.neutralLight,
			fontSize: 12,
			padding: '2px 7px',
			marginRight: 5,
			marginBottom: 10,
			whiteSpace: 'nowrap',
		},
	});

const buttonStyles = {
	root: {
		marginRight: 8,
	},
};

const paragraphStackStyles = {
	root: {width: '30%', height: '100%'},
} as IStackStyles;

const requirementStackStyles = {
	root: {width: '70%', height: '100%'},
} as IStackStyles;

const iconButtonStyles = {
	root: {marginTop: '5px'},
};

const searchboxStyles = {
	root: {marginTop: '5px', width: '50%'},
};

const tokens = {
	childrenGap: 8,
};

const getScrollablePaneStyles = (
	overflowX: string,
): Partial<IScrollablePaneStyles> => {
	return {
		root: {
			position: 'relative',
			width: '100%',
			height: '100%',
		},
		contentContainer: {overflowX},
	};
};

const selectedParagraphsScrollablePaneStyles: Partial<IScrollablePaneStyles> =
	getScrollablePaneStyles('hidden');

const assignRequirementsScrollablePaneStyles: Partial<IScrollablePaneStyles> =
	getScrollablePaneStyles('scroll');

const aggregateButtonStyles = {
	root: {
		width: 'fit-content',
	},
} as IButtonStyles;
